import { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Tooltip,
	CircularProgress
} from '@mui/material';
import { motion } from 'motion/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageBreadcrumb from 'src/components/PageBreadcrumb';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import useThemeMediaQuery from '@fuse/hooks/useThemeMediaQuery';
import useUser from '@auth/useUser';
import { selectIsApplicationsLoading } from '@/components/theme-layouts/components/navbar/navbarSlice';
import { useGetAddressesListQuery } from '@/store/services/address';
import { useGetDashboardStatsQuery } from '@/store/services/reports';
import useGetCurrentPlan from '@/pages/apps/billing/utilities/hooks/useGetCurrentPlan';

/**
 * The analytics dashboard app header.
 */
function AnalyticsDashboardAppHeader() {
	const { data: user } = useUser();
	const navigate = useNavigate();
	const isApplicationsLoading = useSelector(selectIsApplicationsLoading);
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
	const [isNavigationLoading, setIsNavigationLoading] = useState(false);
	const [noAddressDialogOpen, setNoAddressDialogOpen] = useState(false);

	const { data: addressesResponse } = useGetAddressesListQuery({});
	const addresses = addressesResponse?.data || [];

	const { data: dashboardStats } = useGetDashboardStatsQuery(undefined, {
		refetchOnMountOrArgChange: true
	});

	const { currentPlanInfo, isLoading: isLoadingCurrnetPlan } = useGetCurrentPlan();

	const allAddressesDisabled = useMemo(() => {
		if (addresses.length === 0) return false;
		return addresses.every((address: { application?: unknown }) => address.application !== null);
	}, [addresses]);

	const shouldDisabledAssetButton = useMemo(() => {
		const totalAssets = dashboardStats?.data?.total_applications_assets_count;
		const allowedApplications = currentPlanInfo?.number_of_applications;

		return (
			(totalAssets === 2 && allowedApplications === 2) ||
			(totalAssets === 1 && allowedApplications === 1)
		);
	}, [dashboardStats?.data?.total_applications_assets_count, currentPlanInfo?.number_of_applications]);

	const handleNewApplicationClick = (e: React.MouseEvent) => {
		e.preventDefault();

		if (addresses.length === 0) {
			setNoAddressDialogOpen(true);
			return;
		}

		setIsNavigationLoading(true);
		navigate('/apps/applications/new');
	};

	const planLimitTooltip =
		currentPlanInfo != null
			? `Plan limit reached — You have reached the maximum number of addresses allowed in your current plan (${currentPlanInfo.number_of_applications}). One application is allowed per address.`
			: 'Plan limit reached';

	const addNewAssetDisabled =
		isLoadingCurrnetPlan ||
		isNavigationLoading ||
		shouldDisabledAssetButton ||
		allAddressesDisabled;

	const addNewAssetButton = (
		<Button
			variant="contained"
			color="secondary"
			onClick={handleNewApplicationClick}
			disabled={addNewAssetDisabled}
			size={isMobile ? 'small' : 'medium'}
		>
			{isNavigationLoading ? (
				<CircularProgress
					size={20}
					color="inherit"
				/>
			) : (
				<FuseSvgIcon size={20}>heroicons-outline:plus</FuseSvgIcon>
			)}
			<span className="mx-1 sm:mx-2">Add New Asset</span>
		</Button>
	);

	useEffect(() => {
		return () => {
			setIsNavigationLoading(false);
		};
	}, []);

	return (
		<>
			<Dialog
				open={noAddressDialogOpen}
				onClose={() => setNoAddressDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>No address found</DialogTitle>
				<DialogContent>
					<Typography className="text-gray-700">
						Currently there is no address. First add an address then you can add a new asset.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setNoAddressDialogOpen(false)}
						disabled={isNavigationLoading}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="secondary"
						onClick={() => {
							setNoAddressDialogOpen(false);
							navigate('/apps/addresses');
						}}
					>
						Add Address
					</Button>
				</DialogActions>
			</Dialog>

			<div className="flex w-full container mx-4 mb-2">
				<div className="flex flex-col sm:flex-row flex-auto sm:items-end min-w-0 p-6 md:p-8 pl-0 md:pl-0 pb-0 md:pb-0 w-full justify-between gap-4">
					<div className="flex flex-col flex-auto min-w-0">
						<PageBreadcrumb className="mb-2" />
						<Typography className="text-4xl font-bold tracking-tight leading-8">
							Analytics Dashboard
						</Typography>
					</div>
					{user?.role === 'Applicant' && !isApplicationsLoading && (
						<motion.div
							className="flex shrink-0"
							initial={{ opacity: 0, x: 12 }}
							animate={{ opacity: 1, x: 0, transition: { delay: 0.15 } }}
						>
							{shouldDisabledAssetButton ? (
								<Tooltip
									title={planLimitTooltip}
									arrow
									placement="top"
								>
									<span className="inline-flex">{addNewAssetButton}</span>
								</Tooltip>
							) : (
								addNewAssetButton
							)}
						</motion.div>
					)}
				</div>
			</div>
		</>
	);
}

export default AnalyticsDashboardAppHeader;
